import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import ChatIcon from '@mui/icons-material/Chat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { fetchClients } from '@src/api/actions/clients';
import clientInboxApi from '@src/api/clientInbox';
import { useMessage } from '@src/context/common';

const ALL_CLIENTS = '__all__';

const SUGGESTIONS = [
  'How are we pacing this month?',
  'What zips are live?',
  'Show me campaign performance',
  'Compare this month vs last month',
  'Show me recent leads',
];

function AgentChatPage() {
  const message = useMessage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Session state
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // In All Clients mode, track which client a new chat is for
  const [newChatClientId, setNewChatClientId] = useState('');

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isAllClients = selectedClientId === ALL_CLIENTS;

  const clientsById = {};
  clients.forEach((c) => { clientsById[c.id] = c; });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load clients
  useEffect(() => {
    fetchClients()
      .then((response) => {
        const data = response.data?.results || response.data || [];
        const normalized = (Array.isArray(data) ? data : []).map((client) => ({
          id: String(client.id),
          name: client.name || 'Unnamed client',
        }));
        normalized.sort((a, b) => a.name.localeCompare(b.name));
        setClients(normalized);
      })
      .catch(() => {
        message.error('Unable to load clients.');
      });
  }, [message]);

  // Load sessions when client selection changes
  const loadSessions = useCallback(() => {
    if (!selectedClientId) {
      setSessions([]);
      setActiveSessionId(null);
      setTurns([]);
      return;
    }
    setSessionsLoading(true);
    const params = { ordering: '-updated_at' };
    if (!isAllClients) {
      params.client = selectedClientId;
    }
    clientInboxApi
      .listChatSessions(params)
      .then((response) => {
        const data = response.data?.results || response.data || [];
        setSessions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        message.error('Unable to load sessions.');
      })
      .finally(() => {
        setSessionsLoading(false);
      });
  }, [isAllClients, message, selectedClientId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Load history when session changes
  const loadHistory = useCallback(() => {
    if (!activeSessionId) {
      setTurns([]);
      return;
    }
    setLoading(true);
    clientInboxApi
      .getChatHistory({ session: activeSessionId, limit: 50 })
      .then((response) => {
        setTurns(response.data?.turns || []);
        setTimeout(scrollToBottom, 100);
      })
      .catch(() => {
        message.error('Unable to load chat history.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [activeSessionId, message, scrollToBottom]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Start new chat
  const handleNewChat = useCallback(() => {
    setActiveSessionId(null);
    setTurns([]);
    setInputValue('');
    setNewChatClientId('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleSelectSession = useCallback((sessionId) => {
    setActiveSessionId(sessionId);
  }, []);

  // Determine the effective client for sending
  const activeSession = sessions.find((s) => s.id === activeSessionId);
  const effectiveClientId = isAllClients
    ? (activeSession ? String(activeSession.client) : newChatClientId)
    : selectedClientId;
  const effectiveClientName = clientsById[effectiveClientId]?.name || '';

  // Send message
  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || !effectiveClientId || sending) return;

    const tempUserTurn = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: trimmed,
      created_at: new Date().toISOString(),
    };
    setTurns((prev) => [...prev, tempUserTurn]);
    setInputValue('');
    setSending(true);
    setTimeout(scrollToBottom, 50);

    clientInboxApi
      .sendChatMessage({
        client: effectiveClientId,
        message: trimmed,
        session: activeSessionId || undefined,
      })
      .then((response) => {
        const data = response.data || {};
        const assistantTurn = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: data.content || '',
          tool_calls: data.tool_calls || [],
          created_at: new Date().toISOString(),
        };
        setTurns((prev) => [...prev, assistantTurn]);
        setTimeout(scrollToBottom, 50);

        if (data.session_id && !activeSessionId) {
          setActiveSessionId(data.session_id);
          loadSessions();
        }
      })
      .catch((err) => {
        console.error('Chat send failed', err);
        message.error('Unable to get a response. Please try again.');
      })
      .finally(() => {
        setSending(false);
        inputRef.current?.focus();
      });
  }, [activeSessionId, effectiveClientId, inputValue, loadSessions, message, scrollToBottom, sending]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Always show input when a client scope is selected (single or All Clients)
  const canSend = !!selectedClientId;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        p: { xs: 1, md: 3 },
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <SmartToyIcon color="primary" />
          <Typography variant={isMobile ? 'h6' : 'h5'}>Lead Gurus Agent</Typography>
        </Stack>
        <FormControl size="small" sx={{ minWidth: isMobile ? 160 : 260 }}>
          <InputLabel>Client</InputLabel>
          <Select
            label="Client"
            value={selectedClientId}
            onChange={(event) => {
              setSelectedClientId(event.target.value);
              setActiveSessionId(null);
              setTurns([]);
              setNewChatClientId('');
            }}
          >
            <MenuItem value={ALL_CLIENTS}>
              <em>All Clients</em>
            </MenuItem>
            {clients.map((client) => (
              <MenuItem key={client.id} value={client.id}>
                {client.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Main layout: sidebar + chat */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 2 }}>
        {/* Session sidebar */}
        {selectedClientId && !isMobile ? (
          <Card
            sx={{
              width: 280,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewChat}
                size="small"
              >
                New Chat
              </Button>
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {sessionsLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={20} />
                </Box>
              ) : sessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  No conversations yet
                </Typography>
              ) : (
                <List dense disablePadding>
                  {sessions.map((session) => (
                    <ListItemButton
                      key={session.id}
                      selected={session.id === activeSessionId}
                      onClick={() => handleSelectSession(session.id)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        borderLeft: session.id === activeSessionId ? '3px solid' : '3px solid transparent',
                        borderColor: session.id === activeSessionId ? 'primary.main' : 'transparent',
                      }}
                    >
                      <ChatIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                      <ListItemText
                        primary={session.title || 'Untitled chat'}
                        secondary={
                          isAllClients
                            ? (session.client_name || 'Unknown client')
                            : new Date(session.updated_at).toLocaleDateString()
                        }
                        primaryTypographyProps={{
                          variant: 'body2',
                          noWrap: true,
                          fontWeight: session.id === activeSessionId ? 600 : 400,
                        }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Card>
        ) : null}

        {/* Chat area */}
        <Card
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: { xs: 1.5, md: 3 },
              py: 2,
              bgcolor: 'grey.50',
            }}
          >
            {!selectedClientId ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  Select a client to start chatting with the Lead Gurus agent.
                  <br />
                  Or choose &ldquo;All Clients&rdquo; to see every conversation.
                </Typography>
              </Box>
            ) : loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={32} />
              </Box>
            ) : turns.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Stack spacing={1.5} alignItems="center">
                  <SmartToyIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography variant="body1" color="text.secondary" textAlign="center">
                    {activeSession
                      ? `Continue your conversation about ${effectiveClientName}.`
                      : effectiveClientName
                        ? `Start a new conversation about ${effectiveClientName}.`
                        : isAllClients
                          ? 'Pick a client below and ask anything.'
                          : 'Select a session or start a new chat.'}
                  </Typography>
                  {(effectiveClientName || isAllClients) ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                      {SUGGESTIONS.map((suggestion) => (
                        <Chip
                          key={suggestion}
                          label={suggestion}
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setInputValue(suggestion);
                            inputRef.current?.focus();
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </Box>
            ) : (
              <Stack spacing={2}>
                {/* Show client chip in All Clients mode */}
                {isAllClients && effectiveClientName ? (
                  <Chip
                    label={effectiveClientName}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ alignSelf: 'center' }}
                  />
                ) : null}
                {turns.map((turn) => (
                  <ChatBubble key={turn.id} turn={turn} isMobile={isMobile} />
                ))}
                {sending ? (
                  <Box display="flex" alignItems="center" gap={1} pl={6}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Thinking...
                    </Typography>
                  </Box>
                ) : null}
                <div ref={messagesEndRef} />
              </Stack>
            )}
          </Box>

          {/* Input */}
          {canSend ? (
            <Box
              sx={{
                px: { xs: 1.5, md: 3 },
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              {/* Mobile: new chat button */}
              {isMobile && selectedClientId ? (
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleNewChat}>
                    New Chat
                  </Button>
                  {activeSession ? (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      {activeSession.title || 'Untitled'}
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}
              {/* Inline client picker for All Clients mode when starting a new chat */}
              {isAllClients && !activeSessionId ? (
                <FormControl size="small" sx={{ mb: 1, minWidth: 200 }}>
                  <InputLabel>Chatting about</InputLabel>
                  <Select
                    label="Chatting about"
                    value={newChatClientId}
                    onChange={(e) => {
                      setNewChatClientId(e.target.value);
                      setTimeout(() => inputRef.current?.focus(), 50);
                    }}
                  >
                    {clients.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  placeholder={
                    isAllClients && !effectiveClientId
                      ? 'Select a client above, then ask anything...'
                      : `Ask about ${effectiveClientName || 'this client'}...`
                  }
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending || (isAllClients && !effectiveClientId)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          color="primary"
                          onClick={handleSend}
                          disabled={!inputValue.trim() || sending || (isAllClients && !effectiveClientId)}
                          size="small"
                        >
                          <SendIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Box>
          ) : null}
        </Card>
      </Box>
    </Box>
  );
}

function ChatBubble({ turn, isMobile }) {
  const isUser = turn.role === 'user';
  const isAssistant = turn.role === 'assistant';
  const toolCalls = turn.tool_calls || [];

  return (
    <Stack
      direction="row"
      spacing={1.5}
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      alignItems="flex-start"
    >
      {!isUser ? (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            flexShrink: 0,
          }}
        >
          <SmartToyIcon sx={{ fontSize: 18 }} />
        </Avatar>
      ) : null}

      <Box
        sx={{
          maxWidth: isMobile ? '85%' : '70%',
          bgcolor: isUser ? 'primary.main' : 'background.paper',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2.5,
          px: 2,
          py: 1.5,
          boxShadow: isUser ? 0 : 1,
          border: isUser ? 'none' : '1px solid',
          borderColor: 'divider',
        }}
      >
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
          >
            {turn.content}
          </Typography>
        ) : (
          <Box
            sx={{
              '& p': { m: 0, mb: 1, lineHeight: 1.6, fontSize: '0.875rem' },
              '& p:last-child': { mb: 0 },
              '& strong': { fontWeight: 600 },
              '& h1, & h2, & h3, & h4': {
                mt: 1.5,
                mb: 0.5,
                fontWeight: 600,
                lineHeight: 1.3,
              },
              '& h3': { fontSize: '0.95rem' },
              '& h4': { fontSize: '0.875rem' },
              '& ul, & ol': { m: 0, mb: 1, pl: 2.5, fontSize: '0.875rem' },
              '& li': { mb: 0.25, lineHeight: 1.5 },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 1.5,
                mt: 0.5,
                fontSize: '0.8rem',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              },
              '& thead': {
                bgcolor: 'grey.100',
              },
              '& th': {
                textAlign: 'left',
                fontWeight: 600,
                borderBottom: '2px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 0.75,
                whiteSpace: 'nowrap',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                color: 'text.secondary',
              },
              '& td': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 0.75,
              },
              '& tr:nth-of-type(even)': {
                bgcolor: 'grey.50',
              },
              '& tr:last-child td': { borderBottom: 'none' },
              '& hr': { my: 1.5, border: 'none', borderTop: '1px solid', borderColor: 'divider' },
              '& code': {
                bgcolor: 'action.hover',
                px: 0.5,
                borderRadius: 0.5,
                fontSize: '0.8rem',
                fontFamily: 'monospace',
              },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.content}</ReactMarkdown>
          </Box>
        )}
        {isAssistant && toolCalls.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {toolCalls.map((tc, idx) => (
              <Chip
                key={`${tc.name}-${idx}`}
                icon={<BuildIcon sx={{ fontSize: 14 }} />}
                label={tc.name?.replace(/_/g, ' ') || 'tool'}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 22 }}
              />
            ))}
          </Stack>
        ) : null}
      </Box>

      {isUser ? (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'grey.400',
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 18 }} />
        </Avatar>
      ) : null}
    </Stack>
  );
}

export default AgentChatPage;
